import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { Plus, ShieldAlert } from "lucide-react";

import { EmployeesPageLayoutTabBar } from "~/components/employees/layout-tab-bar";
import { PageWrapper } from "~/components/page-wrapper";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";

export const Route = createFileRoute("/_authenticated/employees")({
  component: EmployeesLayout,
});

function EmployeesLayout() {
  const navigate = useNavigate();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();

  // Permission checks
  const canViewEmployees = hasPermission("viewEmployees");
  const canAddEmployee = hasPermission("addEmployee");
  const canEditEmployee = hasPermission("editEmployee");

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="View and manage POS employee accounts"
        title="Manage Employees"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked - show info message
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="View and manage POS employee accounts"
        title="Manage Employees"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view employees
  if (!canViewEmployees) {
    return (
      <PageWrapper
        description="View and manage POS employee accounts"
        title="Manage Employees"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view employee accounts. Contact your
            manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="View and manage POS employee accounts"
      title="Manage Employees"
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <EmployeesPageLayoutTabBar />
          <div className="flex gap-2">
            {/* V4.1: Navigate to route-based manage roles modal */}
            {canEditEmployee && (
              <Button
                onClick={() => navigate({ to: "/employees/manage-roles" })}
                size="lg"
                type="button"
                variant="outline"
              >
                Manage Roles
              </Button>
            )}
            {/* V4.1: Navigate to route-based add employee modal */}
            {canAddEmployee && (
              <Button
                onClick={() => navigate({ to: "/employees/new" })}
                size="lg"
                type="button"
              >
                <Plus className="size-4" />
                Add Employee
              </Button>
            )}
          </div>
        </div>
        <Separator />
        <Outlet />
      </div>
    </PageWrapper>
  );
}
